import React, { useEffect, useLayoutEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuthContext } from "./Context/AuthContext";
import { useSubscriptionAccess } from "./Context/SubscriptionAccessContext";
import { isDashboardRoute } from "./utils/dashboardRoutes";
import { shouldBypassAuthLoading } from "./utils/authLoadingBypass";
import {
  canAccessPathWithSubscription,
  getSubscriptionRedirectForPath,
  isSubscriptionRestrictedPath,
  STATUS_ERROR,
  STATUS_NOT_PURCHASED
} from "./utils/subscriptionValidityUtils";
import "./Layout/dashboard-layout.css";

const Loading = () => {
  const location = useLocation();
  const dashboard = isDashboardRoute(location.pathname);

  useLayoutEffect(() => {
    if (dashboard) {
      document.body.classList.add("dashboard-active");
    }
  }, [dashboard]);

  if (dashboard) {
    return (
      <div className="dashboard-loading-screen">
        <div className="dashboard-loading-screen-inner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="app-loading-screen">
      <div className="app-loading-screen-inner">Loading...</div>
    </div>
  );
};

const AuthRoute = () => {
  const { loggedIn, fetching } = useAuthContext();
  let location = useLocation();
  if (fetching) {
    if (shouldBypassAuthLoading(location.pathname)) {
      return <Outlet />;
    }
    return <Loading />;
  }
  if (loggedIn) {
    return <Outlet />;
  }
  return <Navigate to="/login" state={{ from: location }} replace />;
};

const EnsureHasProfile = () => {
  const { fetching, profile } = useAuthContext();
  let location = useLocation();
  if (fetching) {
    if (shouldBypassAuthLoading(location.pathname)) {
      return <Outlet />;
    }
    return <Loading />;
  }
  if (profile) {
    return <Outlet />;
  }
  return <Navigate to="/profile" state={{ from: location }} replace />;
};

const EnsureVerified = () => {
  const { fetching, userVerify } = useAuthContext();
  let location = useLocation();
  if (fetching) {
    if (shouldBypassAuthLoading(location.pathname)) {
      return <Outlet />;
    }
    return <Loading />;
  }
  if (userVerify) {
    return <Outlet />;
  }
  return <Navigate to="/verify" state={{ from: location }} replace />;
};

const RedirectIfVerified = () => {
  const { fetching, userVerify } = useAuthContext();
  let location = useLocation();
  if (fetching) {
    return <Loading />;
  } else if (userVerify) {
    return <Navigate to="/profile" state={{ from: location }} replace />;
  }
  return <Outlet />;
};

const GuestRoute = () => {
  const { loggedIn, fetching } = useAuthContext();
  let location = useLocation();
  if (fetching) {
    if (location.pathname === "/login") {
      return <Outlet />;
    } else {
      return <Loading />;
    }
  } else if (loggedIn) {
    return (
      <Navigate to={location.state?.from?.pathname || "/home"} state={{ from: location }} replace />
    );
  }
  return <Outlet />;
};

/**
 * Blocks Engine Config / API Generator / Get Token until subscriptionvaliditycheck
 * confirms Active. Does not assume expired/missing while the check is in progress.
 */
const EnsureSubscriptionFeature = () => {
  const location = useLocation();
  const { refreshSubscriptionValidity, openSubscribeModal } = useSubscriptionAccess();
  const [gateStatus, setGateStatus] = useState(null);
  const [verifying, setVerifying] = useState(true);

  useEffect(() => {
    if (!isSubscriptionRestrictedPath(location.pathname)) {
      setGateStatus("active");
      setVerifying(false);
      return undefined;
    }

    let cancelled = false;
    setVerifying(true);
    setGateStatus(null);

    const verify = async () => {
      const result = await refreshSubscriptionValidity();
      if (cancelled) return;
      const nextStatus = result?.status ?? STATUS_ERROR;
      setGateStatus(nextStatus);
      if (nextStatus === STATUS_NOT_PURCHASED) {
        openSubscribeModal();
      }
      setVerifying(false);
    };

    verify();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, refreshSubscriptionValidity, openSubscribeModal]);

  if (!isSubscriptionRestrictedPath(location.pathname)) {
    return <Outlet />;
  }

  if (verifying || gateStatus == null) {
    return <Outlet />;
  }

  if (!canAccessPathWithSubscription(gateStatus, location.pathname)) {
    const redirectTo =
      getSubscriptionRedirectForPath(gateStatus, location.pathname) || "/subscription";
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
};

export {
  AuthRoute,
  GuestRoute,
  EnsureHasProfile,
  EnsureVerified,
  RedirectIfVerified,
  EnsureSubscriptionFeature
};
